import { Component, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import { BaseToastComponent } from '../../../@commons/ui-components/toast-popup/base-toast-component';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { SmartAlertsApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/smart-alerts/smart-alerts-api';
import { PreferenceItemResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/smart-alerts/preferences.response';
import { UpdatePreferencesRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/smart-alerts/update-preferences.request';
import { AlertSeverity } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/alert-severity.enum';
import { ErrorStateComponent } from '../../../@commons/ui-components/error-state/error-state.component';
import { SwitchComponent } from '../../../@commons/ui-components/switch/switch.component';
import { SkeletonLoaderComponent } from '../../../@commons/@components/loaders/skeleton-loader/skeleton-loader.component';
import { InfoIconComponent } from '../../../@icons/info-icon.component';
import { ChevronRightIconComponent } from '../../../@icons/chevron-right.component';

@Component({
  selector: 'vw-notification-preferences-toast',
  imports: [SharedModule, TranslatePipe, ErrorStateComponent, SwitchComponent, SkeletonLoaderComponent, InfoIconComponent, ChevronRightIconComponent],
  templateUrl: './notification-preferences-toast.component.html',
  styleUrl: './notification-preferences-toast.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: SmartAlertsApi, useClass: SmartAlertsApi }],
})
export class NotificationPreferencesToastComponent
  extends BaseToastComponent
  implements OnChanges
{
  preferences: PreferenceItemResponse[] = [];
  loading = false;
  /** Les réglages n'ont pas pu être lus : rien à afficher, rien à enregistrer. */
  loadError = false;
  /** Enregistrement refusé : dit au-dessus du bouton. */
  saveFailed = false;
  saving = false;

  /**
   * Categorie dont l'aide est dépliée, ou `null`.
   *
   * Une seule à la fois : « Croissance » ou « Entretien » ne disent rien tant
   * qu'on n'a pas vu à quoi ressemble l'alerte. Tout afficher d'office
   * noierait les réglages, qui sont ce que l'utilisateur vient chercher.
   */
  expanded: string | null = null;

  toggleInfo(category: string): void {
    this.expanded = this.expanded === category ? null : category;
  }

  /** Catégorie dont les trois niveaux sont dépliés, ou `null` : une ligne fermée par défaut. */
  severityOpen: string | null = null;

  toggleSeverity(category: string): void {
    this.severityOpen = this.severityOpen === category ? null : category;
  }

  /** Choisir un niveau le retient et replie la liste : la valeur s'affiche sur la ligne. */
  chooseSeverity(p: PreferenceItemResponse, severity: AlertSeverity): void {
    p.minPushSeverity = severity;
    this.severityOpen = null;
  }

  severityLabel(severity: AlertSeverity | string | null | undefined): string {
    return this.severityOptions.find((o) => o.value === severity)?.label ?? 'profile.notification-preferences.severities.WARNING';
  }

  /**
   * Niveau des alertes envoyées sur le téléphone : une ligne « Quelles alertes ?
   * Importantes › » qui déplie ces trois choix (avant : une liste déroulante
   * native par catégorie, plus grosse que le reste, répétée cinq fois).
   */
  readonly severityOptions = [
    {
      value: AlertSeverity.INFO,
      label: 'profile.notification-preferences.severities.INFO',
    },
    {
      value: AlertSeverity.WARNING,
      label: 'profile.notification-preferences.severities.WARNING',
    },
    {
      value: AlertSeverity.CRITICAL,
      label: 'profile.notification-preferences.severities.CRITICAL',
    },
  ];

  constructor(private smartAlertsApi: SmartAlertsApi) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    // (Re)load the preferences each time the toast is opened.
    if (changes['isOpen'] && this.isOpen) {
      this.expanded = null;
      this.severityOpen = null;
      this.load();
    }
  }

  async load() {
    this.loading = true;
    try {
      const res = await this.smartAlertsApi.getPreferences();
      this.preferences = res?.preferences ?? [];
      this.loadError = false;
    } catch (e) {
      // Une feuille vide après un 429 laissait croire qu'il n'y avait rien à
      // régler, et « Enregistrer » aurait envoyé une liste vide.
      console.error('NotificationPreferencesToast: failed to load preferences', e);
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  }

  async save() {
    this.saving = true;
    this.saveFailed = false;
    try {
      const request = new UpdatePreferencesRequest();
      request.preferences = this.preferences.map((p) => ({
        category: p.category,
        isEnabled: p.isEnabled,
        pushEnabled: p.pushEnabled,
        minPushSeverity: p.minPushSeverity,
      }));
      await this.smartAlertsApi.updatePreferences(request);
      this.onClose(true);
    } catch (e) {
      // Refusé : la feuille reste ouverte avec les réglages, et le dit.
      console.error('NotificationPreferencesToast: failed to save preferences', e);
      this.saveFailed = true;
    } finally {
      this.saving = false;
    }
  }
}
